import { HttpClient } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { saveAs } from 'file-saver';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest } from 'rxjs';
import { finalize, first, map, switchMap, tap } from 'rxjs/operators';

import { DocumentViewerDialogComponent } from 'dialog/document-viewer-dialog/document-viewer-dialog.component';
import { IDocument } from 'document/document';
import { IModule } from 'module/module';

@Component({
	selector: 'spr-message-attachment',
	templateUrl: './message-attachment.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DialogService],
	host: {
		class: 'flex items-stretch',
	},
})
export class MessageAttachmentComponent implements OnChanges {
	public isDownloading: boolean;
	public attachmentPath: string;
	public attachmentIconClass: string;
	public attachmentUrl$: Observable<string>;
	public attachmentName$: Observable<string>;

	@Input() public userId: string;
	@Input() public module: IModule;
	@Input() public document: IDocument;
	@Input() public visioSessionId: string;

	@Output() public visioOpen = new EventEmitter<string>();

	constructor(
		private _http: HttpClient,
		private _dialogService: DialogService,
		private _changeRef: ChangeDetectorRef
	) {}

	public ngOnChanges(changes: SimpleChanges): void {
		if (
			(!this.module && !this.document && !this.visioSessionId) ||
			!this.userId
		) {
			return;
		}

		if (!!this.module) {
			this.attachmentName$ = this.module.localization$.pipe(
				map((localization) => localization.name)
			);
			this.attachmentPath = `/activity/${this.module.id}/user/${this.userId}`;
			this.attachmentIconClass = 'spreeng spreeng-box';
		} else if (!!this.document) {
			this.attachmentName$ = this.document.localization$.pipe(
				map((localization) => localization.name)
			);
			this.attachmentUrl$ = this.document.localization$.pipe(
				map((localization) => localization.url)
			);
			this.attachmentIconClass =
				this.document.type === 'IMAGE'
					? 'spreeng spreeng-picture'
					: 'spreeng spreeng-file-text';
		} else if (!!this.visioSessionId) {
			this.attachmentIconClass = 'fas fa-video';
		}
	}

	public onShowDocumentClick() {
		combineLatest([this.attachmentName$, this.attachmentUrl$])
			.pipe(
				tap(([name, url]) =>
					this._dialogService.open(DocumentViewerDialogComponent, {
						dismissableMask: true,
						data: { url },
						header: name,
						width: '90%',
					})
				),
				first()
			)
			.subscribe();
	}

	public onDownloadDocumentClick() {
		combineLatest([this.attachmentName$, this.attachmentUrl$])
			.pipe(
				tap((_) => (this.isDownloading = true)),
				switchMap(([name, url]) =>
					this._http
						.get(url, { responseType: 'blob' })
						.pipe(tap((blob) => saveAs(blob, name)))
				),
				finalize(() => {
					this._changeRef.markForCheck();
					this.isDownloading = false;
				}),
				first()
			)
			.subscribe();
	}

	public onOpenVisioClick(sessionId: string) {
		this.visioOpen.emit(sessionId);
	}
}
